<!--
  This component is responsible for rendering the top banners on the page.
  - It includes the SkipToMainContent and CookiesBanner components.
  - The height of the top banners is calculated based on the height of the SkipToMainContent and CookiesBanner components.
  - The height of the top banners is set as a CSS variable '--top-banner-height' on the document element.
  - The height of the top banners is updated whenever the height of the SkipToMainContent or CookiesBanner components changes.
-->
<template>
  <div :class="{ 'banner-cntr': props.isHeaderScrollable }">
    <CookiesBanner
      v-show="
        getCookiesBannerState() &&
        route.path != '/authenticate' &&
        !route.path.startsWith('/child-registration')
      "
      id="cookiesBanner"
      ref="cookiesBanner"
      class="banner"
      @height-change="handleHeightChange"
    />
    <!-- As part of https://btsservice.atlassian.net/browse/NEMO-19546,
     the STMC feature has been implemented for desktop devices only-->
    <SkipToMainContent
      v-show="route.path != '/institution-request' && device.isDesktop"
      :hdr-height="props.hdrHeight"
      :is-header-scrollable="props.isHeaderScrollable"
      @height-change="handleHeightChange"
    />
  </div>
</template>

<script setup>
/**
 * Props
 * - hdrHeight: Height of the header
 */
const props = defineProps({
  hdrHeight: {
    type: Number,
    required: true
  },
  isHeaderScrollable: {
    type: Boolean,
    default: false
  }
});
const route = useRoute();
const device = useDevice();
/**
 * Component height map
 * Used to store the (height) vertical space consumed by the top banners in the UI
 * - skipToMainContent: space consumed by the SkipToMainContent banner
 * - cookiesBanner: space consumed by the CookiesBanner
 */
const compHtMap = ref({
  skipToMainContent: 0,
  cookiesBanner: 0
});
const cookiesBanner = shallowRef(null);
const { calcCookiesBannerState, getCookiesBannerState } = useCookieBanner();
calcCookiesBannerState();

/**
 * Handle the height change event from the child components
 * @param {string} component - Component name. Posiible values are 'skipToMainContent' and 'cookiesBanner'
 * @param {number} newHeight - New height of the component
 * @returns void
 */
const handleHeightChange = (component, newHeight) => {
  if (Object.hasOwn(compHtMap.value, component)) {
    compHtMap.value[component] = newHeight;
  }
};
watch(
  compHtMap,
  (newVal) => {
    document.documentElement.style.setProperty(
      '--top-banner-height',
      newVal.skipToMainContent + newVal.cookiesBanner + 'px'
    );
  },
  { deep: true }
);
</script>

<style lang="scss" scoped>
.banner {
  position: static !important;
  width: 100vw;
}
.banner-cntr {
  position: fixed;
  z-index: 100;
}
</style>
<style lang="scss">
.green-header-layout {
  margin-top: var(--top-banner-height) !important;
}
</style>
